import React, { useMemo } from 'react';

import { HStack, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { Calories } from 'components/price-and-calories';
import { useEnableCartV2Experiment } from 'experiments/use-enable-cart-v2-experiment';
import useEffectOnce from 'hooks/use-effect-once';
import { useFormatCalories } from 'hooks/use-format-calories';
import { CustomEventNames, logRBIProductsDetailsEvent } from 'state/amplitude';
import { useMenuContext } from 'state/menu';
import { useUIContext } from 'state/ui';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';

import {
  UpsellItemButton,
  UpsellItemCalories,
  UpsellItemDetails,
  UpsellItemIcon,
  UpsellItemName,
  UpsellItemPicture,
  UpsellItemPrice,
} from './styles';
import { IUpsellItemProps } from './types';

const UpsellItem: React.FC<React.PropsWithChildren<IUpsellItemProps>> = ({ item, addToCard }) => {
  const { formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const { pricingFunction } = useMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const itemCalories = item.nutritionWithModifiers?.calories;
  const enableCartV2 = useEnableCartV2Experiment();

  const price = useMemo(() => formatCurrencyForLocale(pricingFunction({ item })), [
    item,
    pricingFunction,
    formatCurrencyForLocale,
  ]);

  const calories = useMemo(() => formatCalories(itemCalories), [formatCalories, itemCalories]);

  useEffectOnce(() => {
    logRBIProductsDetailsEvent({
      name: CustomEventNames.UPSELL_DISPLAYED,
      products: [item],
      price: pricingFunction({ item }) / 100,
    });
  });

  const addItemLabel = formatMessage({ id: 'addItemLabel' }, { itemName: item.name?.locale });
  const upsellPriceAndCaloriesLabel = `${price}. ${calories}.`;
  const upsellItemLabel = `${addItemLabel}. ${upsellPriceAndCaloriesLabel}.`;

  return (
    <UpsellItemButton
      testID="upsell-add-to-order-btn"
      accessibilityLabel={upsellItemLabel}
      accessibilityRole="button"
      onPress={() => {
        hapticImpact({ style: enableCartV2 ? HapticsImpactStyle.Light : HapticsImpactStyle.Heavy });
        addToCard(item);
      }}
      {...(enableCartV2 && {
        borderWidth: 0,
      })}
    >
      <UpsellItemPicture image={item.image} alt={item.name?.locale} />
      <UpsellItemDetails accessibilityLabel={upsellPriceAndCaloriesLabel}>
        <UpsellItemName
          style={{ userSelect: 'none' }}
          testID={`upsell-name-${item._id}`}
          accessibilityLevel={3}
        >
          {item.name?.locale}
        </UpsellItemName>
        <HStack>
          <UpsellItemPrice testID={`upsell-price-${item._id}`}>
            <Text style={{ userSelect: 'none' }} testID={`upsell-price-text-${item._id}`}>
              {price}
            </Text>
          </UpsellItemPrice>
          <Calories>
            <Text style={{ userSelect: 'none' }} variant="copyOne">
              •
            </Text>
            <UpsellItemCalories
              style={{ userSelect: 'none' }}
              testID={`upsell-calories-${item._id}`}
            >
              {calories}
            </UpsellItemCalories>
          </Calories>
        </HStack>
      </UpsellItemDetails>
      <UpsellItemIcon
        variant="bkUpsellPlus"
        title={upsellItemLabel}
        accessibilityRole="button"
        accessibilityLabel={upsellItemLabel}
        {...(enableCartV2 && {
          color: 'transparent',
          size: '30',
          variant: 'bkUpsellPlusV2',
          marginLeft: '$2',
        })}
      />
    </UpsellItemButton>
  );
};

export default UpsellItem;
